<template>
	<div v-if="showBanner" ref="bannerRef" class="base-banner bg-color-rust py-100">
		<div class="container">
			<div class="position-relative text-align-center">
				<slot> Override Me </slot>

				<span class="close-button">
					<slot name="close-button">
						<BaseLinkButton data-test="close-banner" classes="modal-close" @click="closeBanner">
							<BaseSvgIcon name="close" />
						</BaseLinkButton>
					</slot>
				</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { getItemFromSessionStorageAsBoolean, setItemInSessionStorage } from '@utils/web-storage';
import { useAppStore } from '@stores/app';
import { useElementBounding } from '@vueuse/core';
interface Props {
	dismissalKey?: string;
}

interface Emits {
	(e: 'banner-closed'): void;
}

const props = withDefaults(defineProps<Props>(), {
	dismissalKey: ''
});

const emit = defineEmits<Emits>();

const appStore = useAppStore();

const bannerRef = ref<HTMLElement | null>(null);
const { height: bannerHeight } = useElementBounding(bannerRef);

const showBanner = computed((): boolean => {
	return !getItemFromSessionStorageAsBoolean(props.dismissalKey);
});

watch(bannerHeight, (height) => {
	if (height > 0) {
		document.documentElement.style.setProperty('--banner-height', `${height}px`);
	}
});

onBeforeUnmount(() => {
	document.documentElement.style.removeProperty('--banner-height');
});

function closeBanner(): void {
	const height = bannerRef.value?.offsetHeight ?? 0;
	setItemInSessionStorage(props.dismissalKey, 'true');
	appStore.updateCurrentBanner(null);

	// Offset the page "jump" when the added page top padding is removed
	window.scrollBy(0, -height);

	// Emit event so parent components can react to dismissal
	emit('banner-closed');
}

function setup(): void {
	if (!showBanner.value) {
		appStore.updateCurrentBanner(null);
	}
}
setup();
</script>
<style lang="scss">
@use '../../styles/constants/layout';
@use '../../styles/constants/colors.scss' as *;
@use '../../styles/utilities/respond-to.scss' as *;

$desktop-min-height: 56px;
$mobile-min-height: 80px;

$desktop-nav-height: 68px;
$mobile-nav-height: 48px;

.base-banner {
	position: fixed;
	top: 0;
	right: 0;
	left: 0;
	color: white;
	min-height: $desktop-min-height;
	z-index: 5000;

	a {
		&:hover,
		&:focus {
			color: white;
			text-decoration: underline;
		}
	}

	.close-button {
		a {
			color: white;
			padding: 4px;
			&:hover,
			&:focus {
				color: white;
				background-color: rgba(token('content-primary'), 0.2);
				border-radius: 5px;
			}
		}
	}

	@include respond-to(smo) {
		min-height: $mobile-min-height;

		& ~ .toast-container {
			.toast {
				margin-top: var(--banner-height);
			}
		}
	}

	@include respond-to(md) {
		& ~ .toast-container {
			.toast {
				margin-top: var(--banner-height);
			}
		}
	}
}

.page {
	padding-top: var(--banner-height, 0px);

	.main-nav {
		top: var(--banner-height, 0px);
	}

	.mobile-nav {
		&__top-bar {
			top: var(--banner-height, 0px);
		}
	}

	.page__main .jump-link-nav {
		top: calc(#{$mobile-nav-height} + var(--banner-height, 0px));

		&--section {
			scroll-margin-top: calc(120px + var(--banner-height, 0px));
		}
	}

	@include respond-to(md) {
		.scrolling-table .table thead {
			top: 7.5rem;
		}
	}

	@include respond-to(lg) {
		.page__main .jump-link-nav {
			top: calc(#{$desktop-nav-height} + var(--banner-height, 0px));
		}
	}
}
</style>
